// src/components/GlobalSearch.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, useLanguage } from '../useTranslations';
import searchService from '../services/SearchService';
import { FiSearch, FiX } from 'react-icons/fi';
import './GlobalSearch.css';

const DEBOUNCE_MS = 200;

const MODULE_COLOR_MAP = {
  'bwra': 'var(--color-bwra)',
  'geographical-risk': 'var(--color-georisk)',
  'customer-risk': 'var(--color-customerrisk)',
  'transaction-monitoring': 'var(--color-txmon)',
  'periodic-reporting': 'var(--color-prpt)',
  'admin': 'var(--color-admin)',
  'dashboard': 'var(--color-primary)',
};

const GlobalSearch = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  useLanguage(); // reactive re-renders on language change

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Keyboard shortcut: Escape to close
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setActiveIndex(-1);
      return;
    }
    const timer = setTimeout(() => {
      const groups = searchService.search(query);
      setResults(groups);
      setActiveIndex(-1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Flatten results for keyboard navigation
  const flatResults = results.flatMap(g =>
    g.results.map(r => ({ ...r, moduleId: g.moduleId, moduleKey: g.moduleKey }))
  );

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && flatResults[activeIndex]) {
        handleNavigate(flatResults[activeIndex].route);
      }
    }
  }, [activeIndex, flatResults]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigate = (route) => {
    navigate(route);
    onClose();
  };

  const hasResults = results.length > 0;
  const isEmpty = query.trim().length >= 2 && !hasResults;

  let flatIdx = 0;

  return (
    <div className="global-search-overlay" onClick={onClose}>
      <div
        className="global-search-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('globalSearch.title')}
      >
        <div className="global-search-input-row">
          <FiSearch className="global-search-icon" size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            className="global-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('globalSearch.placeholder')}
            aria-label={t('globalSearch.placeholder')}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              className="global-search-clear"
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              aria-label={t('common.close')}
            >
              <FiX size={16} />
            </button>
          )}
          <button
            className="global-search-close-btn"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <kbd>Esc</kbd>
          </button>
        </div>

        {query.trim().length > 0 && query.trim().length < 2 && (
          <div className="global-search-hint">{t('globalSearch.typeMore')}</div>
        )}

        {isEmpty && (
          <div className="global-search-empty">{t('globalSearch.noResults')}</div>
        )}

        {hasResults && (
          <div className="global-search-results" ref={listRef} role="listbox">
            {results.map((group) => (
              <div key={group.moduleId} className="global-search-group">
                <div
                  className="global-search-group-header"
                  style={{ borderLeftColor: MODULE_COLOR_MAP[group.moduleId] || 'var(--color-primary)' }}
                >
                  <span
                    className="global-search-module-dot"
                    style={{ background: MODULE_COLOR_MAP[group.moduleId] || 'var(--color-primary)' }}
                    aria-hidden="true"
                  />
                  <span
                    className="global-search-group-name global-search-module-link"
                    onClick={() => handleNavigate(group.route)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleNavigate(group.route)}
                  >
                    {t(group.moduleKey)}
                  </span>
                </div>
                {group.results.map((result) => {
                  const currentIdx = flatIdx++;
                  const isActive = currentIdx === activeIndex;
                  return (
                    <button
                      key={result.id}
                      className={`global-search-result${isActive ? ' active' : ''}`}
                      onClick={() => handleNavigate(result.route)}
                      role="option"
                      aria-selected={isActive}
                      onMouseEnter={() => setActiveIndex(currentIdx)}
                    >
                      <span className="global-search-result-title">{result.title}</span>
                      {result.subtitle && (
                        <span className="global-search-result-subtitle">{result.subtitle}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {!query && (
          <div className="global-search-hint">{t('globalSearch.hint')}</div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;
